import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Banner } from "@astryxdesign/core/Banner";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { useStudio } from "@/features/studio/StudioRoot";
import { TimeStep } from "@/features/booking/TimeStep";
import { publicApi } from "@/lib/api/public";
import type { PublicBooking, Slot } from "@/lib/api/schemas";
import { formatDateTime, localDateOf } from "@/lib/format";
import { errorMessage } from "@/lib/errors";
import { useT } from "@/lib/i18n";

/** Reschedule: pick a new time, confirm. A failed move leaves the original booking intact (server-side). */
export function RescheduleSheet({ booking, token, onClose, onDone }: {
  booking: PublicBooking;
  token: string;
  onClose: () => void;
  onDone: (b: PublicBooking) => void;
}) {
  const studio = useStudio();
  const t = useT();
  const qc = useQueryClient();
  const service = studio.services.find((s) => s.id === booking.service_id);
  const [date, setDate] = useState(localDateOf(booking.starts_at, studio.timezone));
  const [slot, setSlot] = useState<Slot | null>(null);
  const key = useRef(crypto.randomUUID());
  const move = useMutation({
    mutationFn: (s: Slot) => publicApi.reschedule(token, s.starts_at, key.current),
    onSuccess: (b) => {
      void qc.invalidateQueries({ queryKey: ["slots", studio.slug] });
      onDone(b);
    },
    onError: () => {
      key.current = crypto.randomUUID();
      void qc.invalidateQueries({ queryKey: ["slots", studio.slug] });
    },
  });

  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} showSwipeHandle>
      <DrawerContent>
        <VStack gap={4} paddingInline={4} paddingBlockStart={2} className="sheet-footer overflow-y-auto">
          <DrawerTitle className="text-start text-lg font-semibold">{t.reschedule.title}</DrawerTitle>
          <DrawerDescription className="text-start">{t.reschedule.now(formatDateTime(booking.starts_at, studio.timezone))}</DrawerDescription>
          {!service ? (
            <Banner status="warning" title={t.reschedule.serviceGoneTitle} description={t.reschedule.serviceGoneText} collapsible={false} />
          ) : slot ? (
            <VStack gap={3}>
              <Text type="large">{t.reschedule.newTime(formatDateTime(slot.starts_at, studio.timezone))}</Text>
              {move.isError ? <Banner status="error" title={t.reschedule.failed} description={`${errorMessage(move.error)} ${t.reschedule.keptSuffix}`} collapsible={false} /> : null}
              <Button label={t.reschedule.confirm} variant="primary" size="lg" width="100%" isLoading={move.isPending} onClick={() => move.mutate(slot)} />
              <Button label={t.reschedule.pickOther} variant="ghost" width="100%" onClick={() => { setSlot(null); move.reset(); }} />
            </VStack>
          ) : (
            <TimeStep studio={studio} service={service} date={date} onDate={setDate} onPick={setSlot} />
          )}
        </VStack>
      </DrawerContent>
    </Drawer>
  );
}
